import { useMemo, useState } from 'react';
import { Box, Button, Grid, MenuItem, TextField, Tooltip, Typography } from '@leap4/frontend';
import { CalculatedMetricTargetMode } from '../../../api/generated/models/calculatedMetricTargetMode';
import {
  buildCatalogKeySet,
  buildOverrideTargetKeySet,
  calcMetricFamily,
  calcRowReportFamily,
  clampDecimals,
  deriveKeyFromLabel,
  deriveOverrideCalcKey,
  FormulaOperandOption,
  LabelOption,
  metricKeyReportFamily,
  operandReportFamily,
} from './calculatedMetrics.constants';
import type { CalculatedMetricRowState } from './campaignForm.types';
import ActiveToggleButton from './ActiveToggleButton';
import InternalOnlyToggleButton from './InternalOnlyToggleButton';
import FormulaBuilder from './FormulaBuilder';
import { buildReportTypeIndex, renderReportTypeChips } from './ReportTypeChips';
import { useReportingMetadata } from '../reporting/hooks/useReportingMetadata';
import { validateCalculatedMetricRow } from './formulaValidator';

interface CalculatedMetricRowProps {
  row: CalculatedMetricRowState;
  index: number;
  allRows: CalculatedMetricRowState[];
  overrideTargetOptions: LabelOption[];
  operandOptions: FormulaOperandOption[];
  validEventKeys: string[];
  onChange: (index: number, patch: Partial<CalculatedMetricRowState>) => void;
  onRemove: (index: number) => void;
  onNotify: (message: string) => void;
}

const CalculatedMetricRow = ({
  row,
  index,
  allRows,
  overrideTargetOptions,
  operandOptions,
  validEventKeys,
  onChange,
  onRemove,
  onNotify,
}: CalculatedMetricRowProps) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const metadata = useReportingMetadata();
  const reportTypeIndex = useMemo(() => buildReportTypeIndex(metadata), [metadata]);

  const overrideTargetKeys = useMemo(
    () => buildOverrideTargetKeySet(overrideTargetOptions),
    [overrideTargetOptions]
  );

  const catalogKeys = useMemo(
    () => buildCatalogKeySet(metadata.metrics, metadata.dimensions),
    [metadata.metrics, metadata.dimensions]
  );

  const existingKeys = useMemo(() => {
    const set = new Set<string>(catalogKeys);
    validEventKeys.forEach((eventKey) => set.add(eventKey));
    allRows.forEach((other) => {
      if (other._rowId !== row._rowId && other.key) {
        set.add(String(other.key).trim());
      }
    });
    return set;
  }, [allRows, validEventKeys, row._rowId, catalogKeys]);

  const rowOperandOptions = useMemo<FormulaOperandOption[]>(() => {
    const siblingCalc: FormulaOperandOption[] = allRows
      .filter((other) => other._rowId !== row._rowId && String(other.key || '').trim().length > 0)
      .map((other) => ({
        kind: 'metric',
        key: String(other.key).trim(),
        label: `${String(other.display_label || other.key).trim()} (Calculated)`,
        family: calcMetricFamily(other),
      }));
    return [...operandOptions, ...siblingCalc];
  }, [operandOptions, allRows, row._rowId]);

  const operandFamily = useMemo(() => calcRowReportFamily(row.formula?.tokens), [row.formula]);
  const targetFamily = useMemo(
    () =>
      row.target_mode === CalculatedMetricTargetMode.override && row.target_column
        ? metricKeyReportFamily(row.target_column)
        : null,
    [row.target_mode, row.target_column]
  );
  const rowFamily = operandFamily ?? targetFamily;

  const filteredOverrideTargetOptions = useMemo(
    () =>
      operandFamily
        ? overrideTargetOptions.filter((o) => metricKeyReportFamily(o.key) === operandFamily)
        : overrideTargetOptions,
    [overrideTargetOptions, operandFamily]
  );

  const filteredOperandOptions = useMemo(
    () =>
      targetFamily
        ? rowOperandOptions.filter((o) => {
            const fam = operandReportFamily(o);
            return fam === null || fam === targetFamily;
          })
        : rowOperandOptions,
    [rowOperandOptions, targetFamily]
  );

  const familyMismatch =
    operandFamily && targetFamily && operandFamily !== targetFamily
      ? 'Override target and formula must be on the same report (Impression Date vs Conversion Date).'
      : null;

  const errors = useMemo(
    () =>
      validateCalculatedMetricRow(row, allRows, validEventKeys, overrideTargetKeys, catalogKeys),
    [row, allRows, validEventKeys, overrideTargetKeys, catalogKeys]
  );

  const handleModeChange = (targetMode: CalculatedMetricTargetMode) => {
    const patch: Partial<CalculatedMetricRowState> = { target_mode: targetMode };
    if (targetMode === CalculatedMetricTargetMode.override && row.target_column) {
      patch.key = deriveOverrideCalcKey(row.target_column);
    }
    if (targetMode === CalculatedMetricTargetMode.new) {
      patch.target_column = undefined;
      if (!row._keyEdited) {
        patch.key = deriveKeyFromLabel(row.display_label || '', existingKeys);
      }
    }
    onChange(index, patch);
  };

  const handleTargetColumnChange = (targetColumn: string) => {
    onChange(index, {
      target_column: targetColumn,
      key: deriveOverrideCalcKey(targetColumn),
    });
  };

  const handleDisplayLabelChange = (label: string) => {
    const patch: Partial<CalculatedMetricRowState> = { display_label: label };
    if (row.target_mode === CalculatedMetricTargetMode.new && !row._keyEdited) {
      patch.key = deriveKeyFromLabel(label, existingKeys);
    }
    onChange(index, patch);
  };

  const formatPreview = `${row.format?.prefix || ''}${(1234.5).toFixed(
    clampDecimals(row.format?.decimals ?? 2)
  )}${row.format?.suffix || ''}`;

  const handleSaveRow = () => {
    onNotify(
      "Changes saved and will take effect only when the 'Update Campaign' button is pressed!"
    );
    onChange(index, { _collapsed: true });
  };

  if (row._collapsed) {
    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: errors.length > 0 ? 'error.main' : 'divider',
          borderRadius: 2,
          px: 2,
          py: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography sx={{ fontWeight: 600 }} noWrap>
            {row.display_label || '(unnamed calculated metric)'}
            {errors.length > 0 && (
              <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                — has errors
              </Typography>
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              onClick={() => onChange(index, { _collapsed: false })}
              sx={{ textTransform: 'none' }}
            >
              Edit
            </Button>
            <ActiveToggleButton
              active={row.is_active !== false}
              onToggle={(next) => onChange(index, { is_active: next })}
            />
            <InternalOnlyToggleButton
              internalOnly={row.is_internal_only !== false}
              onToggle={(next) => onChange(index, { is_internal_only: next })}
            />
            <Button variant="outlined" color="error" onClick={() => onRemove(index)}>
              Remove
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: errors.length > 0 ? 'error.main' : 'divider',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <TextField
              select
              label="Mode"
              value={row.target_mode}
              onChange={(e) => handleModeChange(e.target.value as CalculatedMetricTargetMode)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value={CalculatedMetricTargetMode.new}>New metric</MenuItem>
              <MenuItem value={CalculatedMetricTargetMode.override}>Override metric</MenuItem>
            </TextField>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Appears on:
              </Typography>
              {rowFamily ? (
                renderReportTypeChips([rowFamily])
              ) : (
                <Typography variant="caption" sx={{ fontStyle: 'italic', opacity: 0.6 }}>
                  add a metric to determine
                </Typography>
              )}
            </Box>

            {row.target_mode === CalculatedMetricTargetMode.override && (
              <Typography
                variant="caption"
                sx={{ color: 'warning.main', fontWeight: 700, flex: 1, textAlign: 'center', px: 1 }}
              >
                ⚠ Override replaces the target metric entirely — values, display label, and format
                all come from this calculated metric. Any custom label or other setting previously
                applied to that metric is ignored.
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ActiveToggleButton
                active={row.is_active !== false}
                onToggle={(next) => onChange(index, { is_active: next })}
              />
              <InternalOnlyToggleButton
                internalOnly={row.is_internal_only !== false}
                onToggle={(next) => onChange(index, { is_internal_only: next })}
              />
              <Button variant="outlined" color="error" onClick={() => onRemove(index)}>
                Remove
              </Button>
            </Box>
          </Box>
        </Grid>

        {row.target_mode === CalculatedMetricTargetMode.override && (
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Target column"
              fullWidth
              value={row.target_column || ''}
              onChange={(e) => handleTargetColumnChange(String(e.target.value))}
            >
              <MenuItem value="">Choose target</MenuItem>
              {filteredOverrideTargetOptions.map((option) => (
                <MenuItem
                  key={option.key}
                  value={option.key}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <span>{option.value}</span>
                  {renderReportTypeChips(reportTypeIndex.get(option.key) ?? [])}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        <Grid item xs={12} md={row.target_mode === CalculatedMetricTargetMode.override ? 5 : 9}>
          <TextField
            label="Display label"
            fullWidth
            value={row.display_label}
            onChange={(e) => handleDisplayLabelChange(e.target.value)}
            helperText="The header shown for this metric in the report and CSV."
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            label="Order"
            type="number"
            fullWidth
            value={row.display_order ?? index}
            onChange={(e) => onChange(index, { display_order: Number(e.target.value) })}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Formula
          </Typography>
          <FormulaBuilder
            tokens={row.formula?.tokens || []}
            operandOptions={filteredOperandOptions}
            onChange={(tokens) => onChange(index, { formula: { tokens } })}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Display format
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Text wrapped around the number. Preview: <strong>{formatPreview}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Prefix (before value)"
            fullWidth
            placeholder="$"
            value={row.format?.prefix || ''}
            onChange={(e) => onChange(index, { format: { ...row.format, prefix: e.target.value } })}
            helperText="e.g. $ → $1234.50"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Suffix (after value)"
            fullWidth
            placeholder="%"
            value={row.format?.suffix || ''}
            onChange={(e) => onChange(index, { format: { ...row.format, suffix: e.target.value } })}
            helperText="Label only — it does not scale the value. For a percentage, multiply the formula by 100."
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Decimals"
            type="number"
            fullWidth
            inputProps={{ min: 0, max: 6 }}
            value={row.format?.decimals ?? 2}
            onChange={(e) =>
              onChange(index, {
                format: { ...row.format, decimals: clampDecimals(Number(e.target.value)) },
              })
            }
            helperText="Digits after the decimal point (0–6)."
          />
        </Grid>

        {row.target_mode === CalculatedMetricTargetMode.new && (
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
            {showAdvanced ? (
              <Tooltip
                arrow
                placement="top"
                title="Internal id used in the data grid and CSV export. Auto-generated from the Display label; set it manually only if you need a specific id."
              >
                <TextField
                  label="Column id"
                  fullWidth
                  value={row.key}
                  onChange={(e) =>
                    onChange(index, {
                      key: e.target.value.trim().toLowerCase(),
                      _keyEdited: true,
                    })
                  }
                  helperText="Lowercase letters, numbers, underscores."
                  InputProps={{
                    endAdornment: (
                      <Button
                        size="small"
                        onClick={() => setShowAdvanced(false)}
                        sx={{ textTransform: 'none', minWidth: 0 }}
                      >
                        Hide
                      </Button>
                    ),
                  }}
                />
              </Tooltip>
            ) : (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  onClick={() => setShowAdvanced(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Advanced Settings
                </Button>
              </Box>
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              {row.target_mode === CalculatedMetricTargetMode.override && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                  Internal column id: {row.key || '(auto-derived on target select)'}
                </Typography>
              )}
              {errors.map((error) => (
                <Typography key={error} variant="body2" color="error">
                  {error}
                </Typography>
              ))}
              {familyMismatch && (
                <Typography variant="body2" color="error">
                  {familyMismatch}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              disableElevation
              onClick={handleSaveRow}
              disabled={errors.length > 0 || !!familyMismatch}
            >
              Save changes
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CalculatedMetricRow;
